import React, { useState, useEffect, useRef } from 'react'
import useSurveillanceReportingState from '../../stores/SurveillanceReportingState'
import {
  List,
  ListItem,
  Paper,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material'
import { toDomain } from '~/mappers/surveillance-reporting-mapper'
import {
  State,
  useHookstate,
} from '@hookstate/core'
import { ReportComment } from './components/ReportComment'
import ReportMenuList from './components/ReportMenuList'
import { ISurveillanceReportingReponse } from '../../interfaces/ISurveillanceReporting'
import useUserState from '~/features/User/stores/UserState'
import useDispatchState from '~/features/Dispatch/stores/DispatchState'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useLang } from '~/hooks/useLang'
import { listSurveillanceReportingByDispatchId } from '../../services'

interface IVideoMonitoringCards {
  readonly onlyCurrentOccurrence?: boolean
  readonly height?: number
}

// eslint-disable-next-line max-lines-per-function
export default function VideoMonitoringCards({
  onlyCurrentOccurrence = false,
  height,
}: IVideoMonitoringCards) {
  const { user: userSession } = useUserState()
  const { translate } = useLang()
  const { dispatch } = useDispatchState()
  const { id: dispatchId } = dispatch()
  const {
    surveillanceReportingStates,
    listSurveillanceReporting,
  } = useSurveillanceReportingState()
  const { loading } = surveillanceReportingStates()
  const {
    surveillanceReporting,
    save,
    showCheckbox,
    reports,
    switchCheck,
    newNarrations,
    isEditMode,
    isAssociateMode,
  } = surveillanceReportingStates()
  const newNarrationsValue = newNarrations.get()
  const [isFetching, setIsFetching] = useState(false)
  const { cameraId, notes, reportedAt, occurrenceId, id } =
    surveillanceReporting
  const reportsNoFiltered = useHookstate<
    readonly ISurveillanceReportingReponse[]
  >([])
  const rowCount = useHookstate(0)
  const reportId = useHookstate<number>(-1)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)
  const idMenu = open ? 'menu-report' : ''
  const disabledAssociate = useHookstate<number | null>(null)
  const reportFiltered = showCheckbox.get()
    ? reports.filter(
      (reportFiltered) =>
        !reportFiltered.occurrenceId.get() &&
        reportFiltered.userId.get() === userSession().id.get()
    )
    : reports

  const handleClose = () => {
    setAnchorEl(null)
  }

  const findIndex = () => {
    const idReport = reportId.get()
    const findReport = reports.findIndex((rep) => rep.id.get() === idReport)
    return findReport
  }

  const handleEditReport = () => {
    const index = findIndex()
    save.set(true)
    isEditMode.set(true)
    isAssociateMode.set(false)
    cameraId.set(reports[index].cameraId.get())
    notes.set(reports[index].notes.get())
    reportedAt.set(reports[index].reportedAt.get())
    occurrenceId.set(reports[index].occurrenceId.get())
    id.set(reports[index].id.get())
    handleClose()
  }

    const handleAssociateReport = () => {
    const index = findIndex()
    
    reports[index].set((prev) => ({
      ...prev,
      checked: true,
    }))
    
    showCheckbox.set(true)
    isAssociateMode.set(true)
    isEditMode.set(false)
    save.set(false)
    handleClose()
  }

  const treatData = (
    reset: boolean,
    data: readonly unknown[],
    rowsCount: number
  ) => {
    if (reset) {
      loading.set(true)
      const toDomainsReport = data.map((report: unknown) =>
        toDomain(report as ISurveillanceReportingReponse)
      )
      reports.set(toDomainsReport)
      reportsNoFiltered.set(toDomainsReport)
      rowCount.set(rowsCount)
    } else {
      loading.set(true)
      const toDomainsReport = data.map((report: unknown) =>
        toDomain(report as ISurveillanceReportingReponse)
      )
      reports.merge(toDomainsReport)
      reportsNoFiltered.set(toDomainsReport)
      rowCount.set(rowsCount)
    }
  }

  const nextdata = async (reset = false) => {
    if (isFetching) return
    setIsFetching(true)
    
    const currentReports = reports.get()
    const pageQuery = reset ? 0 : Math.floor(currentReports.length / 10)
    const pageQueryListByDispatch = reset
      ? 0
      : Math.floor(currentReports.length / 5)

    try {
      if (onlyCurrentOccurrence) {
        const response = await listSurveillanceReportingByDispatchId({
          dispatchId: dispatchId.get(),
          page: pageQueryListByDispatch,
        })
        if (response.data) {
          treatData(reset, response.data, response.rowsCount)
        }
      } else {
        const filterParams = {
          page: pageQuery,
          limit: 10,
          userId: switchCheck.get()
            ? (userSession().id.get() as number)
            : undefined,
        }
        
        const { data, rowsCount } = await listSurveillanceReporting(filterParams)
        treatData(reset, data, rowsCount)
      }
    } catch (error) {
      console.error('Erro ao buscar dados:', error)
    } finally {
      loading.set(false)
      setIsFetching(false)
    }
  }

  const lastNewNarrationsValue = useRef(newNarrationsValue)

  useEffect(() => {
    if (lastNewNarrationsValue.current === newNarrationsValue) return
    
    lastNewNarrationsValue.current = newNarrationsValue
    
    const fetchData = async () => {
      if (isFetching) return
      setIsFetching(true)
      
      try {
        if (onlyCurrentOccurrence) {
          const response = await listSurveillanceReportingByDispatchId({
            dispatchId: dispatchId.get(),
            page: 0,
          })
          if (response.data) {
            loading.set(true)
            const toDomainsReport = response.data.map((report: unknown) =>
              toDomain(report as ISurveillanceReportingReponse)
            )
            reports.set(toDomainsReport)
            reportsNoFiltered.set(toDomainsReport)
            rowCount.set(response.rowsCount)
          }
        } else {
          const filterParams = {
            page: 0,
            limit: 10,
            userId: switchCheck.get()
              ? (userSession().id.get() as number)
              : undefined,
          }
          
          const { data, rowsCount } = await listSurveillanceReporting(filterParams)
          loading.set(true)
          const toDomainsReport = data.map((report: unknown) =>
            toDomain(report as ISurveillanceReportingReponse)
          )
          reports.set(toDomainsReport)
          reportsNoFiltered.set(toDomainsReport)
          rowCount.set(rowsCount)
        }
      } catch (error) {
        console.error('Erro ao buscar dados:', error)
      } finally {
        loading.set(false)
        setIsFetching(false)
      }
    }
    
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newNarrationsValue, onlyCurrentOccurrence, dispatchId.get(), switchCheck.get()])

  React.useEffect(() => {
    if (reports.get().length === 0) {
      nextdata(true)
    }
  }, [])

  return (
    loading.get() ? (
      <Box
        sx={{
          width: '100%',
          height: height || 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    ) : (
      <Paper
        variant="outlined"
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: !reports.get().length ? 'center' : 'flex-start',
          borderRadius: 1,
        }}
      >
        {reportFiltered.length === 0 && !loading.get() ? (
          <Box
            sx={{
              width: '100%',
              height: height || 400,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography>
              {showCheckbox.get() 
                ? translate('There are no reports to associate')
                : translate('There are no reports')}
            </Typography>
          </Box>
        ) : (
          <List dense={true} sx={{ width: '100%' }}>
            <InfiniteScroll
              dataLength={reportFiltered.length}
              next={() => nextdata()}
              hasMore={
                !showCheckbox.get() 
                  ? rowCount.get() > 0 && reports.get().length < rowCount.get()
                  : false
              }
              height={height || 400}
              style={{ paddingBottom: '20px' }}
              loader={
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 2,
                  }}
                >
                  <CircularProgress />
                </Box>
              }
            >
              {reportFiltered.map(
                (reportMap: State<ISurveillanceReportingReponse>) => (
                  <ListItem sx={{ padding: '5px 8px' }} key={reportMap.id.get()}>
                    <ReportComment
                      report={reportMap}
                      showCheckbox={showCheckbox.get()}
                      handleClickMenu={(event) => {
                        setAnchorEl(event?.currentTarget)
                        reportId.set(reportMap.id.get())
                        const hasOccurrence = reportMap.occurrenceId.get()
                        const isNotOwner = reportMap.userId.get() !== userSession().id.get()
                        disabledAssociate.set(
                          (hasOccurrence || isNotOwner) ? 1 : null
                        )
                      }}
                    />
                  </ListItem>
                )
              )}
            </InfiniteScroll>
          </List>
        )}
        <ReportMenuList
          handleEditReport={handleEditReport}
          handleAssociateReport={handleAssociateReport}
          idMenu={idMenu}
          handleClose={handleClose}
          anchorEl={anchorEl}
          disabledAssociate={disabledAssociate.get()}
        />
      </Paper>
    )
  )
}
